<template>
  <div class="row">
    <h1 class="page-header text-center">loyalty points</h1>
    <loading-icon v-if="isProgramLoading"/>
    <div v-else class="medium-6 columns small-centered">
      <div class="sb-row bottom-spacing">
        <label for="merchant_loyalty">loyalty points enabled</label>
        <input
          type="checkbox"
          v-model="loyaltyEnabled"
          :disabled="!canEditSettings"
          name="merchant[loyalty]"
          id="merchant_loyalty" />
      </div>

      <div v-show="loyaltyEnabled" class="loyalty-wrapper">
        <div class="sb-row bottom-spacing">
          <label for="per-visit">assign loyalty points per <strong>visit</strong></label>
          <input
            type="radio"
            class="points-radio"
            v-model="programType"
            :disabled="!canEditSettings"
            name="merchant[program_type]"
            id="per-visit"
            value="points_per_visit" />
        </div>

        <div class="sb-row bottom-spacing">
          <label for="per-dollar">assign loyalty points per <strong>dollar spent</strong></label>
          <input
            type="radio"
            class="points-radio"
            v-model="programType"
            :disabled="!canEditSettings"
            @change="pointsPerVisit = Math.floor(pointsPerVisit) || 1"
            name="merchant[program_type]"
            id="per-dollar"
            value="points_per_dollar" />
        </div>

        <div v-if="programType === 'points_per_dollar'" class="bottom-spacing">
          <div class="sb-row start">
            <input
              type="number"
              :disabled="!canEditSettings"
              :class="['small', 'margin-right', { 'has-error': ppdMinError || ppdDecError }]"
              step="0.01" min="0.01"
              v-model="pointsPerDollar"
              name="merchant[points_per_dollar]"
              id="ppd-number-field">
            <label for="ppd-number-field">{{ pointsPerDollar | pluralize('point') }} awarded per dollar spent</label>
          </div>

          <span class="error" v-if="ppdMinError">a minimum of .01 points is allowed</span>
          <span class="error" v-else-if="ppdDecError">only 2 decimal places allowed</span>
        </div>

        <div v-if="programType === 'points_per_visit'" class="bottom-spacing">
          <div class="sb-row start">
            <input
              type="number"
              :disabled="!canEditSettings"
              :class="['small', 'margin-right', {'has-error': ppvMinError || ppvDecError}]"
              step="1" min="1"
              v-model="pointsPerVisit"
              name="merchant[points_per_dollar]"
              id="ppv-number-field">
            <label for="ppv-number-field">{{ pointsPerVisit | pluralize('point') }} awarded per visit</label>
          </div>

          <span class="error" v-if="ppvMinError">a minimum of 1 point is allowed</span>
          <span class="error" v-else-if="ppvDecError">decimal values are not allowed</span>
        </div>
      </div>
    </div>
    <div v-if="loyaltyEnabled" class="collapsible-sections mx-10">
      <collapsible-section
        title="bonus engine"
        :expanded.sync="isBonusEngineExpanded"
      >
        <a @click="addBonusRule" class="absolute right-0 top-0 text-xl self-end mt-3">+ add bonus</a>
        <template v-if="bonusRulesNotDisabled.length && eventTypes.length && Object.values(eventsValues).length">
          <transition-group name="bonus-rules-list" tag="div">
            <bonus-engine-rule v-for="(bonusRule, index) in bonusRulesNotDisabled"
                               :key="bonusRule.unique_id"
                               :bonusRule.sync="bonusRulesNotDisabled[index]"
                               :bonusRulesCount="bonusRulesNotDisabled.length"
                               :programType="programType"
                               :eventTypes="eventTypes"
                               :eventValues="eventsValues[bonusRule.event_type]"
                               @increasePriority="increasePriority"
                               @decreasePriority="decreasePriority"
                               @deleteBonusRule="showDeleteBonusRuleModal"
                               class="m-auto"
            />
          </transition-group>
          <simple-modal
            v-if="bonusRuleToDelete !== null"
            class="confirm-delete-modal"
            title="delete bonus"
            resolveText="delete"
            @rejected="bonusRuleToDelete = null"
            @resolved="deleteBonusRule"
          >
            are you sure you want to<br />delete this bonus ?
          </simple-modal>
        </template>
        <div v-else class="text-2xl text-center text-gray-400 mt-16">bonus engine not configured</div>
      </collapsible-section>
      <collapsible-section
        title="manage loyalty by location"
        :expanded.sync="isLoyaltyByLocationExpanded"
        v-if="hasLoyaltyByLocationMetaEnabled && hasLocations && loyaltyByLocationsEnabled"
      >
        <div v-if="!errorMessage">
          <input v-model="searchText"
                 class="location-search mt-10 mb-0 px-3"
                 type="text"
                 placeholder="search location name"
                 @input.prevent="debounce">
          <loading-icon v-if="areLocationsLoading"/>
          <table v-else-if="hasSearchLocations" id="location-table">
            <thead>
            <tr role="row">
              <sort-table-header
                v-for="(value, index) in columns" :key="index"
                :ariaControlId="'location-table'"
                :defaultSort="value.sort"
                :sortId="value.column"
                :text="value.name"
                :wrapperClasses="'small-3'"
                @sortLocation="sortLocation"></sort-table-header>
              <th class="text-center">loyalty points enabled</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(location, index) in locations" :key="index" class="location-row">
              <td>{{location.display_name}}</td>
              <td>{{location.region}}</td>
              <td :class="{ 'not-edittable': !canEditSettings }" class="location-loyalty-checkbox text-center">
                <sb-checkbox
                  :checked="location.loyalty_points"
                  v-on:update:checked="changedLocationsLoyalty(location)"
                ></sb-checkbox>
              </td>
            </tr>
            </tbody>
          </table>
          <p v-else class="text-center">no location found</p>
          <sb-dotted-pagination
            :page.sync="pageSync"
            :pages.sync="totalPageSync"></sb-dotted-pagination>
        </div>
        <div v-else slot="mainContent" class="text-center error-label">{{errorMessage}}</div>
      </collapsible-section>
      <collapsible-section
        title="points expiration"
        :expanded.sync="isPointsExpirationExpanded"
      >
        <select id="points_expiration_type"
                :disabled="!canEditSettings"
                name="merchant[points_expiration_type]"
                v-model="expirationType"
                class="my-10"
        >
          <option>off</option>
          <option>time based</option>
          <option>visit based</option>
        </select>

        <div v-if="expirationType === 'visit based'">
          <div class="sb-row bottom-spacing">
            <input type="number"
                   :disabled="!canEditSettings"
                   step="1" min="30"
                   :class="['small', 'margin-right', {'has-error': dslvMinError, dslvMaxError}]"
                   v-model="daysSinceLastVisit"
                   id="point_expiration_without_visit"
                   name="merchant[points_expiration_interval]"/>

            <label>
              number of days without visit until points expiration (30 days minimum)
              <span class="error" v-if="dslvMinError">a minimum of 30 days is allowed</span>
              <span class="error" v-if="dslvMaxError">a maximum of 2147483647 days is allowed</span>
            </label>

            <input type="radio"
                   :disabled="!canEditSettings"
                   class="points-radio margin-right"
                   v-model="visitBasedType"
                   value="days_since_last_visit"
                   id="days_since_last_visit">
          </div>

          <div class="sb-row bottom-spacing">
            <input type="number"
                   :disabled="!canEditSettings"
                   step="1" min="30"
                   :class="['small', 'margin-right', {'has-error': dspiMinError, dspiMaxError}]"
                   v-model="daysSincePointsIssued"
                   id="point_expiration_since_visit"
                   name="merchant[points_expiration_interval]"/>

            <label>
              number of days since visit until points removed from balance (30 days minimum)
              <span class="error" v-if="dspiMinError">a minimum of 30 days is allowed</span>
              <span class="error" v-if="dspiMaxError">a maximum of 2147483647 days is allowed</span>
            </label>

            <input type="radio"
                   class="points-radio margin-right"
                   :disabled="!canEditSettings"
                   v-model="visitBasedType"
                   value="days_since_points_issued"
                   id="days_since_points_issued"/>
          </div>
        </div>

        <div v-if="expirationType === 'time based'" class="bottom-spacing">
          <div class="sb-row start">
            points expire on the 1st of every
            <input type="number"
                   :disabled="!canEditSettings"
                   step="1" min="1"
                   class="small time-expiration-input"
                   v-model="monthlyInterval"
                   name="merchant[months_for_points_to_expire]"
                   id="merchant_months_for_points_to_expire">
            {{ monthlyInterval | pluralize('month') }}
          </div>
          <span class="error" v-if="monthlyMinError">a minimum of 1 month is allowed</span>
          <span class="error" v-if="monthlyMaxError">a maximum of {{ maximumMonthlyInterval }} {{ maximumMonthlyInterval | pluralize('month') }} is allowed</span>
        </div>
      </collapsible-section>
    </div>

    <div v-if="showNotice" class="callout notice">settings updated</div>
    <div class="sb-row center bottom-spacing">
      <button @click="clickedSubmit" :disabled="submitting || hasError" class="action-button button proceed">update</button>
    </div>

    <modal
      v-if="showWarningModal"
      :showClose="false"
      :wrapperClasses="['expiration-modal','confirm-send']">
      <h3 slot="header">are you sure you want to set up points expiration?</h3>

      <div slot="body">
        <div v-if="expirationType === 'time based'">
          <span>once points expiration is set up, all customer points collected prior to {{ startDate }} will be erased and the next point expiration period will be {{ endDate }} so members can only spend points accumulated in between</span>

          <div class="bottom-spacing">
            <strong>{{ startDate }} - {{ endDate }}</strong>
          </div>

          <div class="bottom-spacing">set points to expire the 1st of every {{monthlyInterval}} {{ monthlyInterval | pluralize('month') }}?</div>
        </div>

        <div v-if="expirationType === 'visit based' && visitBasedType === 'days_since_last_visit'">
          once points expiration is set up, all customers who have not recorded a visit in the last <strong>{{daysSinceLastVisit}}</strong> {{ daysSincePointsIssued | pluralize('day') }} will have their points set to 0, and whenever a customer goes <strong>{{daysSinceLastVisit}}</strong> {{ daysSinceLastVisit | pluralize('day') }} without recording a visit, their points total will be set to 0.
          <span class="notice">set points to expire after {{daysSinceLastVisit}} {{ daysSinceLastVisit | pluralize('day') }} without a visit?</span>
        </div>

        <div v-if="expirationType === 'visit based' && visitBasedType === 'days_since_points_issued'">
          once points expiration is set up, all points acquired more than <strong>{{daysSincePointsIssued}} {{ daysSincePointsIssued | pluralize('day') }}</strong> ago will be erased and can no longer be redeemed by your members
          <span class="notice">set visit points to expire after <strong>{{daysSincePointsIssued}} {{ daysSincePointsIssued | pluralize('day') }}</strong>?</span>
        </div>
      </div>

      <div class="sb-row center bottom-spacing" slot="action">
        <button class="action-button button proceed" @click.prevent="submitForm()" :disabled="submitting" style="margin-right:20px">set expiration</button>
        <span>or</span>
        <button class="action-button button close" @click.prevent="closeModal()" :disabled="submitting" style="margin-left:20px">cancel</button>
      </div>
    </modal>
  </div>
</template>

<script>
  import moment from "moment-timezone"
  import Modal from '../../components/shared/Modal'
  import LoadingIcon from "../../components/shared/LoadingIcon"
  import SortTableHeader from "../../components/shared/SortTableHeader"
  import ToggleSectionSlide from "../../components/shared/ToggleSectionSlide"
  import UserAbilitiesMixin from '../../mixins/UserAbilitiesMixin'
  import { bonusRulesAPI } from "../../services/api/bonusRulesAPI"
  import { currentMerchantAPI } from "../../services/api/currentMerchant"
  import { loyaltyByLocationsAPI } from "../../services/api/loyaltyByLocations"
  import { mapGetters } from 'vuex'
  import _ from "lodash"
  import { CollapsibleSection } from '@springbig/sb-components'
  import BonusEngineRule from "../../components/ProgramSettings/BonusEngineRule";
  import { parse, parseISO, format, isValid } from "date-fns";
  import { SimpleModal } from "@springbig/sb-components";

    export default {
      name: "Program",
      data() {
        return {
          areLocationsLoading: false,
          columns: [
            {
              column: "display_name",
              name: "location",
              sort: null
            }, {
              column: "region",
              name: "state",
              sort: "asc"
            }
          ],
          daysSinceLastVisit: null,
          daysSincePointsIssued: null,
          duration: 500,
          errorMessage: null,
          expirationType: 'off',
          hasLocations: false,
          isProgramLoading: false,
          locations: [],
          locationsDelta: null,
          loyaltyEnabled: false,
          loyaltyByLocationsEnabled: false,
          bonusRules: [],
          monthlyInterval: 1,
          pageSync: 0,
          pointsPerDollar: null,
          pointsPerVisit: null,
          programType: 'visit',
          programExpiration: {},
          searchText: null,
          showNotice: false,
          showWarningModal: false,
          submitting: false,
          totalPageSync: null,
          maximumMonthlyInterval: 1000,
          visitBasedType: 'days_since_last_visit',
          isBonusEngineExpanded: false,
          isLoyaltyByLocationExpanded: false,
          isPointsExpirationExpanded: false,
          eventTypes: [],
          eventsValues: {},
          debounceUpdateMsg: null,
          bonusRuleToDelete: null,
        }
      },
      mixins: [UserAbilitiesMixin],
      components: {
        Modal,
        LoadingIcon,
        ToggleSectionSlide,
        SortTableHeader,
        CollapsibleSection,
        BonusEngineRule,
        SimpleModal
      },
      computed: {
        merchantId() {
          return this.$route.params.id
        },
        canEditSettings() {
          return this.hasAccess(this.userAbilitiesValue, 'SETTINGS_EDIT')
        },
        dslvMinError() {
          return this.daysSinceLastVisit < 30 && this.expirationType === "visit based" && this.visitBasedType === "days_since_last_visit";
        },
        dslvMaxError() {
          return this.daysSinceLastVisit > 2147483647 && this.expirationType === "visit based" && this.visitBasedType === "days_since_last_visit";
        },
        dspiMinError() {
          return this.daysSincePointsIssued < 30 && this.expirationType === "visit based" && this.visitBasedType === "days_since_points_issued";
        },
        dspiMaxError() {
          return this.daysSincePointsIssued > 2147483647 && this.expirationType === "visit based" && this.visitBasedType === "days_since_points_issued";
        },
        expirationNotChanged() {
          return _.isEqual(this.programExpiration, this.buildExpirationModel())
        },
        monthlyMinError() {
          return this.monthlyInterval < 1 && this.expirationType === "time based";
        },
        monthlyMaxError() {
          return this.monthlyInterval >= this.maximumMonthlyInterval && this.expirationType === "time based";
        },
        endDate() {
          return moment().add(this.monthlyInterval, "M").startOf('month').format('MM/DD/YYYY');
        },
        getSortCol() {
          return this.columns.find(value => value.sort !== null)
        },
        hasError() {
          return this.dspiMinError ||
            this.dspiMaxError ||
            this.dslvMinError ||
            this.dslvMaxError ||
            this.monthlyMinError ||
            this.monthlyMaxError ||
            this.ppdDecError ||
            this.ppdMinError ||
            this.ppvMinError ||
            this.ppvDecError ||
            this.bonusEngineError;
        },
        hasSearchLocations() {
          return !(this.searchText && this.locations.length === 0);
        },
        ...mapGetters(
          ['userAbilitiesValue', 'hasLoyaltyByLocationMetaEnabled']
        ),
        ppdDecError() {
          const regex = new RegExp("(?=[^\0])(?=^([0-9]+){0,1}(\.[0-9]{1,2}){0,1}$)", 'g')
          return !regex.test(this.pointsPerDollar) && this.programType === "points_per_dollar"
        },
        ppdMinError() {
          return this.pointsPerDollar < 0.01 && this.programType === "points_per_dollar";
        },
        ppvMinError() {
          return this.pointsPerVisit < 1 && this.programType === "points_per_visit";
        },
        ppvDecError() {
          return !Number.isInteger(parseFloat(this.pointsPerVisit));
        },
        startDate() {
          return moment().startOf('month').format('MM/DD/YYYY');
        },
        bonusEngineError() {
          return this.bonusRules.some( rule => rule.is_valid!==true )
        },
        bonusRulesNotDisabled() {
          const bonusRulesNotDisabled = this.bonusRules.filter( rule => !rule.disabled_at );
          return _.sortBy(bonusRulesNotDisabled, ['priority']);
        }
      },
      async created() {
        this.locationsDelta = new Map()
        this.$store.dispatch('LOAD_CURRENT_USER')
        this.$store.dispatch('LOAD_CURRENT_MERCHANT')
        this.isProgramLoading = true
        this.eventTypes = await bonusRulesAPI.getEventTypes()
        await this.loadBonusEngineEventsOptions()
        currentMerchantAPI.GETProgramSettings().then( settings => {
          this.loyaltyEnabled = settings.loyalty;
          this.programType = settings.program_type;
          this.pointsPerDollar = settings.points_per_dollar;
          this.pointsPerVisit = settings.points_per_visit;
          this.loyaltyByLocationsEnabled = settings.loyalty_by_location_enabled
          this.bonusRules = this.prepareBonusRules(settings.bonus_rules)

          if(settings.points_expiration_type === null) {
            this.expirationType = "off";
          } else if(settings.points_expiration_type === "days_since_last_visit") {
            this.expirationType = "visit based";
            this.daysSinceLastVisit = settings.points_expiration_interval;
            this.visitBasedType = settings.points_expiration_type;
          } else if(settings.points_expiration_type === "days_since_points_issued") {
            this.expirationType = "visit based";
            this.daysSincePointsIssued = settings.points_expiration_interval;
            this.visitBasedType = settings.points_expiration_type;
          } else if(settings.points_expiration_type === "every_x_months") {
            this.expirationType = "time based";
            this.monthlyInterval = settings.points_expiration_interval;
          }

          this.programExpiration = this.buildExpirationModel()
          this.debounceUpdateMsg = _.debounce(() => this.showNotice=false, 10000)
        }).finally(() => { this.isProgramLoading = false });

        this.debounce = _.debounce(this.debounceSearch, this.duration);
        await this.getLocations()
        this.hasLocations = this.locations?.length > 0
      },
      beforeUnmount() {
        this.debounceUpdateMsg.cancel()
      },
      methods: {
        buildExpirationModel() {
          const expirationKeys = ["expirationType", "monthlyInterval", "visitBasedType", "daysSinceLastVisit", "daysSincePointsIssued"]
          let updatedExpirationData = {}
          expirationKeys.forEach((key) => {
            updatedExpirationData[key] = this[key]
          })
          return updatedExpirationData
        },
        changedLocationsLoyalty(location) {
          location.loyalty_points = !location.loyalty_points
          const delta = {
            id: location.id,
            loyalty_points: location.loyalty_points
          }
          this.locationsDelta.set(location.id, delta)
        },
        changeSortOption(sortValue) {
          return sortValue ? sortValue === "asc" ? "desc" : "asc" : "asc"
        },
        clickedSubmit() {
          if(this.expirationType === "off" || this.expirationNotChanged) {
            this.submitForm();
          } else {
            this.showWarningModal = true;
          }
        },
        closeModal() {
          this.showWarningModal = false;
        },
        debounceSearch() {
          this.areLocationsLoading = true
          this.searchText = this.searchText.trim()
          this.pageSync = 0
          this.getLocations()
        },
        async getLocations() {
          try {
            this.areLocationsLoading = true
            const sortOption = this.getSortCol
            const params = {
              params: {
                page: this.pageSync + 1,
                query: this.searchText,
                sort_column: sortOption.column,
                sort_direction: sortOption.sort
              }
            }
            const data = await loyaltyByLocationsAPI.GETLoyaltyByLocations(params)
            this.setLocations(data)
          }
          catch(error){
            this.errorMessage = `there is an api issue: ${error}`
          }
          finally{
            this.areLocationsLoading = false
          }
        },
        setLocations(data) {
          if (data) {
            this.errorMessage = null
            this.locations = data.merchant_locations
            this.totalPageSync = data.meta.total_pages
            this.locations.forEach((location) => {
            if (this.locationsDelta.has(location.id)) {
              location.loyalty_points = this.locationsDelta.get(location.id).loyalty_points
            }
          })
          } else {
            this.errorMessage = "no found locations"
          }
        },
        sortLocation(sortId) {
          this.columns.forEach(value => {
            value.sort = value.column === sortId ? this.changeSortOption(value.sort) : null
          });
          this.getLocations()
        },
        submitForm() {
          const settings = {
            loyalty: this.loyaltyEnabled,
            program_type: this.programType,
            points_per_dollar: this.pointsPerDollar,
            points_per_visit: this.pointsPerVisit,
            merchant_locations_attributes: Array.from(this.locationsDelta.values())
          };

          if(this.expirationType === "off") {
            settings.points_expiration_type = null;
          } else if(this.expirationType === "visit based" && this.visitBasedType === "days_since_last_visit") {
            settings.points_expiration_type = "days_since_last_visit";
            settings.points_expiration_interval = this.daysSinceLastVisit;
          } else if(this.expirationType === "visit based" && this.visitBasedType === "days_since_points_issued") {
            settings.points_expiration_type = "days_since_points_issued";
            settings.points_expiration_interval = this.daysSincePointsIssued;
          } else if(this.expirationType === "time based") {
            settings.points_expiration_type = "every_x_months";
            settings.points_expiration_interval = this.monthlyInterval;
          }
          settings.bonus_rules_attributes = this.bonusRules.map( rule => {
            const eventRange = {}
            if(rule.event_type === "time_of_day_range") {
              eventRange.event_range_min = this.formatDate12h(rule.event_range_min);
              eventRange.event_range_max = this.formatDate12h(rule.event_range_max);
            }
            return {
              ...rule,
              ...eventRange,
              is_valid: undefined,
              unique_id: undefined,
            }
          })

          this.programExpiration = this.buildExpirationModel()
          this.submitting = true;
          currentMerchantAPI.PUTProgramSettings(this.merchantId, {current_merchant: settings})
            .then(res => {
              this.showWarningModal = false;
              this.showNotice = true;
              this.debounceUpdateMsg();
              this.locationsDelta.clear()
              this.bonusRules = this.prepareBonusRules(res.data.bonus_rules)
            })
            .finally(()=>{
              this.submitting = false;
            });
        },

        toggleLocationSlide() {
          this.isLocationOpen = !this.isLocationOpen
        },
        addBonusRule() {
          this.bonusRules.push({
            event_type: null,
            event_value: null,
            event_range_min: null,
            event_range_max: null,
            points: null,
            priority: this.bonusRulesNotDisabled.length ? Math.max(...this.bonusRulesNotDisabled.map(rule=>rule.priority))+1 : 0,
            is_valid: false,
            disabled_at: null,
            unique_id: _.uniqueId()
          });
        },
        prepareBonusRules(bonusRules) {
          return bonusRules.map( rule => ({
            ...rule,
            is_valid: true,
            disabled_at: null,
            unique_id: _.uniqueId(),
            event_range_min: rule.event_type==="time_of_day_range" ? this.parseDate(rule.event_range_min) : rule.event_range_min,
            event_range_max: rule.event_type==="time_of_day_range" ? this.parseDate(rule.event_range_max) : rule.event_range_max,
          }) )
        },
        deleteBonusRule() {
          // If the rule comes from the database it's disabled, otherwise it's removed
          if(this.bonusRuleToDelete.id !== undefined) {
            this.bonusRuleToDelete.disabled_at = new Date()
          }else{
            this.bonusRules = this.bonusRules.filter( rule => this.bonusRuleToDelete.unique_id !== rule.unique_id )
          }
          this.bonusRuleToDelete = null
          // Reordering priorities
          for(let [key, bonusRule] of this.bonusRulesNotDisabled.entries()) {
            bonusRule.priority = key
          }
        },
        showDeleteBonusRuleModal(bonusRule) {
          this.bonusRuleToDelete = bonusRule
        },
        increasePriority(bonusRule) {
          const targetBonusRule = _.findLast(this.bonusRulesNotDisabled, rule => rule.priority<bonusRule.priority)
          this.swapPriorities(bonusRule, targetBonusRule)
        },
        decreasePriority(bonusRule) {
          const targetBonusRule = _.find(this.bonusRulesNotDisabled, rule => rule.priority>bonusRule.priority)
          this.swapPriorities(bonusRule, targetBonusRule)
        },
        swapPriorities(fromBonusRule, toBonusRule) {
          const currentRulePriority = fromBonusRule.priority
          fromBonusRule.priority = toBonusRule.priority
          toBonusRule.priority = currentRulePriority
        },
        async loadBonusEngineEventsOptions() {
          try {
            const eventsWithOptions = this.eventTypes.filter(event => event.data_url)
            const promises = eventsWithOptions.map(async ({ data_url, key }) => {
              let options = await bonusRulesAPI.getEventOptions(data_url)
              if (key === 'day_of_the_week') options = options.map(day => ({ ...day, label: day.label.toLowerCase() }))
              return { key, options }
            })
            Promise.all(promises).then(result => result.forEach(({ key, options }) => this.eventsValues[key] = options))
            this.errorMessage = null
          }
          catch(err) {
            this.errorMessage = 'error, please refresh or contact support';
          }
        },
        async updateEventValuesFilter(filter) {
          if(!filter) {
            this.filteredEventValues = null;
            this.eventValuesFilter = {}
            return
          }
          const url = this.eventTypes.find(event => event.key === filter.eventType)?.data_url
          this.filteredEventValues = await bonusRulesAPI.getEventOptions(url, { page: filter.page+1, per_page:this.eventsValuesPerPage, search:filter.searchString })
          this.eventValuesFilter = filter
        },
        parseDate(dateStr) {
          if(isValid(parseISO(dateStr))) {
            return parseISO(dateStr);
          }else{
            return parse(dateStr, "h:mm aaaaa'm'", new Date());
          }
        },
        formatDate12h(date) {
          return format(date, "h:mm aaaaa'm'")
        }
      },
      watch: {
        pageSync: function() {
          this.getLocations()
        }
      }
    }
</script>

<style lang="scss" scoped>
.collapsible-sections::v-deep {
  margin: 30px 30px 0;
  > main {
    margin-bottom: 30px;
    &:first-child > .collapsing-container {
      width:100%;
    }
  }
  .collapsing-container {
    margin: auto;
    width: 450px;
  }
  .content-container {
    display: flex;
    flex-direction: column;
    position: relative;
    margin-bottom:4rem;
    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }
}

.confirm-delete-modal::v-deep {
  header {
    font-family: AileronLight,sans-serif;
  }
  .content {
    font-size: 2rem;
    text-align: center;
    padding: 0 30px;
  }
  footer {
    flex-direction: column;
    align-items: center;
    >.sb-button {
      width: 180px;
      padding: 0.5rem 3rem;
    }
    >.sb-reject {
      color: #128ff9;
      background-color: white;
    }
  }
  .close {
    font-size: initial;
    opacity: 0.5
  }
}

.bonus-rules-list-move {
  transition: transform .3s;
}

.bonus-rules-list-enter-active {
  transition: all .3s;
}

.bonus-rules-list-enter {
  opacity: 0;
  transform: translateY(-30px);
}

.location-search {
  border: 1px solid rgba(164,164,164,var(--tw-border-opacity));
  border-radius: 9999px;
}

#location-table th:first-child {
  padding-left: 0;
}

.row .sb-row input.small {
  padding-right: 0;
}
</style>
